import { Link, useForm } from '@inertiajs/react'
import { SlidersHorizontal, WandSparkles } from 'lucide-react'
import { useEffect, useId, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { adminLines } from '@/routes'
import type { Line } from '@/types'

/**
 * Quick fixes in place on the scene page: the script wording and what the AI
 * voice reads ("spoken as"). The line page has the rest (takes, variations).
 */
export function LineEditor({
  line,
  productionId,
  canGenerate,
  onDone,
}: {
  line: Line
  productionId: string
  /** The line has a voice and ElevenLabs is set up. */
  canGenerate: boolean
  /** Closed: saved (asking for a new take or not), or cancelled. */
  onDone: (generated: boolean) => void
}) {
  const spoken = line.kind === 'dialogue' || line.kind === 'song'
  // Fixing an AI-voiced line is usually so you can hear it again: ⌘↩ asks for a new take.
  const generates = spoken && canGenerate
  const form = useForm({ line: { text: line.text, spoken_as: line.spokenAs ?? '' } })
  const text = useRef<HTMLTextAreaElement>(null)
  const id = useId()

  useEffect(() => {
    const el = text.current
    el?.focus()
    el?.setSelectionRange(el.value.length, el.value.length)
  }, [])

  const reworded = form.data.line.text.trim() !== line.text.trim()
  const respoken = form.data.line.spoken_as.trim() !== (line.spokenAs ?? '').trim()
  const error = Object.values(form.errors)[0]
  const recorded = line.audioSource === 'recording' || line.audioSource === 'upload'

  function save(generate: boolean) {
    if (!form.isDirty && !generate) return onDone(false)
    form.transform((data) => (generate ? { ...data, generate: '1' } : data))
    form.patch(adminLines.update({ productionId, id: line.id }).url, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => onDone(generate),
    })
  }

  function keys(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      e.preventDefault()
      onDone(false)
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      save(generates && !e.shiftKey)
    }
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: keyboard shortcuts for the fields inside
    <div className="grid gap-2 rounded-md border bg-muted/30 p-3" onKeyDown={keys}>
      <label htmlFor={`${id}-text`} className="grid gap-1 text-xs font-medium text-muted-foreground">
        <span>Script</span>
        <Textarea
          id={`${id}-text`}
          ref={text}
          rows={1}
          className="min-h-0 bg-background text-sm text-foreground"
          value={form.data.line.text}
          onChange={(e) => form.setData('line.text', e.target.value)}
        />
      </label>
      {error && <p className="text-xs text-destructive">{error}</p>}
      {spoken && (
        <label htmlFor={`${id}-spoken`} className="grid gap-1 text-xs font-medium text-muted-foreground">
          <span>
            Spoken as{' '}
            <span className="font-normal">— what the AI voice reads, if different. Actors see the script.</span>
          </span>
          <Textarea
            id={`${id}-spoken`}
            rows={1}
            className="min-h-0 bg-background text-sm text-foreground"
            placeholder={form.data.line.text}
            value={form.data.line.spoken_as}
            onChange={(e) => form.setData('line.spoken_as', e.target.value)}
          />
        </label>
      )}
      {reworded && recorded && (
        <p className="text-xs text-amber-700 dark:text-amber-500">
          New wording retires the current {line.audioSource === 'recording' ? 'cast recording' : 'upload'} until it’s
          re-recorded.
        </p>
      )}
      {reworded && !respoken && line.spokenAs && (
        <p className="text-xs text-amber-700 dark:text-amber-500">
          The voice still reads the “spoken as” above — update it to match.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {generates && (
          <Button size="sm" disabled={form.processing} onClick={() => save(true)} title="⌘↩">
            <WandSparkles /> Save &amp; generate
          </Button>
        )}
        <Button
          size="sm"
          variant={generates ? 'outline' : 'default'}
          disabled={form.processing}
          onClick={() => save(false)}
          title={generates ? '⌘⇧↩' : '⌘↩'}
        >
          Save
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onDone(false)} title="Esc">
          Cancel
        </Button>
        {spoken && (
          <Button size="sm" variant="ghost" className="ml-auto" asChild>
            <Link href={adminLines.show({ productionId, id: line.id })}>
              <SlidersHorizontal /> Takes &amp; variations
            </Link>
          </Button>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground">
        {generates ? '⌘↩ save & generate · ⌘⇧↩ save only' : '⌘↩ save'} · Esc cancel
      </p>
    </div>
  )
}
